import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import {
    Button,
    Tree,
    TreeItem,
    TreeItemContent
} from 'react-aria-components';
import type { Key } from '@react-types/shared';
import { api } from '../../shared/api/apiClient';
import type { IntegrationDto, CategoryNodeDto, IntegrationCategoriesDto, User } from '../../types';
import { hasCapability } from '../../utils/accessControl';
import '../../shared/styles/common-modal-styles.css';
import '../../shared/styles/common-form-styles.css';
import './CategorySelectionModal.css';
import { buildTree } from '../shared/categoryUtils';

interface CategorySelectionModalProps {
    integration: IntegrationDto | null;
    isOpen: boolean;
    onClose: () => void;
    currentUser?: User | null;
}

interface CategorySelection {
    [categoryExternalId: string]: boolean;
}

interface ClientCategoryDto {
    id: number;
    name: string;
    parentCategoryId?: number;
}

interface ClientCategoryTreeNode extends ClientCategoryDto {
    children: ClientCategoryTreeNode[];
}

const flattenSupplierCategories = (categories: CategoryNodeDto[]): CategoryNodeDto[] => {
    return categories.reduce((acc, category) => {
        acc.push(category);
        if (category.children?.length) {
            acc.push(...flattenSupplierCategories(category.children));
        }
        return acc;
    }, [] as CategoryNodeDto[]);
};

const updateSupplierCategory = (
    categories: CategoryNodeDto[],
    categoryId: number,
    update: (category: CategoryNodeDto) => CategoryNodeDto
): CategoryNodeDto[] => {
    return categories.map(category => {
        const updatedCategory = category.id === categoryId ? update(category) : category;
        if (!updatedCategory.children?.length) {
            return updatedCategory;
        }

        return {
            ...updatedCategory,
            children: updateSupplierCategory(updatedCategory.children, categoryId, update)
        };
    });
};

const buildClientCategoryTree = (categories: ClientCategoryDto[]): ClientCategoryTreeNode[] => {
    const byId = new Map<number, ClientCategoryTreeNode>();
    const roots: ClientCategoryTreeNode[] = [];

    categories.forEach(category => {
        byId.set(category.id, { ...category, children: [] });
    });

    categories.forEach(category => {
        const node = byId.get(category.id)!;
        if (category.parentCategoryId && byId.has(category.parentCategoryId)) {
            byId.get(category.parentCategoryId)!.children.push(node);
            return;
        }

        roots.push(node);
    });

    return roots;
};

const buildClientCategoryPathMap = (categories: ClientCategoryTreeNode[]): Record<number, string> => {
    const result: Record<number, string> = {};

    const visit = (nodes: ClientCategoryTreeNode[], path: string[] = []) => {
        nodes.forEach(node => {
            const fullPath = [...path, node.name];
            result[node.id] = fullPath.length > 1 ? fullPath.slice(1).join(' > ') : fullPath[0];
            visit(node.children, fullPath);
        });
    };

    visit(categories);
    return result;
};

interface SupplierCategoryNodeProps {
    node: CategoryNodeDto;
    selections: CategorySelection;
    selectedCategoryId?: number;
    clientCategoryPathById: Record<number, string>;
    onSelectCategory: (category: CategoryNodeDto) => void;
    onSelectionChange: (categoryExternalId: string, isSelected: boolean) => void;
}

const SupplierCategoryNode: React.FC<SupplierCategoryNodeProps> = ({
    node,
    selections,
    selectedCategoryId,
    clientCategoryPathById,
    onSelectCategory,
    onSelectionChange
}) => {
    const isNodeOrChildrenSelected = (category: CategoryNodeDto): boolean => {
        if (selections[category.externalId || '']) return true;
        return category.children?.some(child => isNodeOrChildrenSelected(child)) ?? false;
    };

    const getSelectionState = (category: CategoryNodeDto): { checked: boolean; indeterminate: boolean } => {
        if (!category.children || category.children.length === 0) {
            return { checked: selections[category.externalId || ''] || false, indeterminate: false };
        }

        const childStates = category.children.map(child => getSelectionState(child));
        const hasAnySelected = childStates.some(state => state.checked || state.indeterminate);
        const allSelected = childStates.every(state => state.checked);

        return {
            checked: allSelected,
            indeterminate: hasAnySelected && !allSelected
        };
    };

    const [expanded, setExpanded] = useState(() => isNodeOrChildrenSelected(node));
    const hasChildren = Boolean(node.children?.length);
    const selectionState = getSelectionState(node);
    const isChecked = hasChildren ? selectionState.checked : (selections[node.externalId || ''] || false);
    const isSelected = selectedCategoryId === node.id;
    const mappedCategory = node.mappedClientCategories?.[0];
    const mappedCategoryPath = mappedCategory
        ? clientCategoryPathById[mappedCategory.id] ?? mappedCategory.name
        : null;

    const handleToggle = (event: React.MouseEvent) => {
        event.stopPropagation();
        if (hasChildren) {
            setExpanded(current => !current);
        }
    };

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (node.externalId) {
            onSelectionChange(node.externalId, event.target.checked);
        }
    };

    const handleOpenBinding = (event: React.MouseEvent) => {
        event.stopPropagation();
        onSelectCategory(node);
    };

    return (
        <div className="category-tree-node">
            <div
                className={`category-tree-item integration-category-bind-row ${hasChildren ? 'expandable' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => onSelectCategory(node)}
            >
                {hasChildren ? (
                    <button
                        type="button"
                        className="category-expand-button"
                        onClick={handleToggle}
                        aria-label={expanded ? 'Свернуть' : 'Развернуть'}
                    >
                        {expanded ? 'v' : '>'}
                    </button>
                ) : (
                    <span className="category-expand-button placeholder" aria-hidden="true" />
                )}
                <span className="category-name">
                    {node.name}
                    {node.externalId && (
                        <span className="category-id text-muted"> {node.externalId}</span>
                    )}
                </span>
                <span
                    className={`category-binding-status ${mappedCategoryPath ? 'mapped' : 'empty'}`}
                    title={mappedCategoryPath ? `${mappedCategoryPath}` : 'Не привязана'}
                >
                    {mappedCategoryPath ? `${mappedCategoryPath}` : 'Не привязана'}
                </span>
                <button
                    type="button"
                    className="category-binding-open-button"
                    onClick={handleOpenBinding}
                >
                    Привязка
                </button>
                <input
                    ref={(element) => {
                        if (element) element.indeterminate = selectionState.indeterminate;
                    }}
                    type="checkbox"
                    checked={isChecked}
                    onChange={handleCheckboxChange}
                    className={`category-checkbox ${selectionState.indeterminate ? 'indeterminate' : ''}`}
                    onClick={(event) => event.stopPropagation()}
                />
                <span className="category-count">
                    {node.children?.length || 0}
                </span>
            </div>
            {expanded && node.children && node.children.length > 0 && (
                <div className="category-children">
                    {node.children.map(child => (
                        <SupplierCategoryNode
                            key={child.id}
                            node={child}
                            selections={selections}
                            selectedCategoryId={selectedCategoryId}
                            clientCategoryPathById={clientCategoryPathById}
                            onSelectCategory={onSelectCategory}
                            onSelectionChange={onSelectionChange}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

interface ClientCategoryTreeProps {
    categories: ClientCategoryTreeNode[];
    selectedCategoryId?: number;
    onSelectCategory: (category: ClientCategoryTreeNode) => void;
}

const ClientCategoryTree: React.FC<ClientCategoryTreeProps> = ({
    categories,
    selectedCategoryId,
    onSelectCategory
}) => {
    const [expandedKeys, setExpandedKeys] = useState<Set<Key>>(
        () => new Set(categories.map(node => node.id))
    );

    const nodeById = useMemo(() => {
        const map = new Map<number, ClientCategoryTreeNode>();
        const visit = (nodes: ClientCategoryTreeNode[]) => {
            nodes.forEach(node => {
                map.set(node.id, node);
                visit(node.children);
            });
        };

        visit(categories);
        return map;
    }, [categories]);

    const handleSelectionChange = useCallback((keys: 'all' | Set<Key>) => {
        if (keys === 'all') {
            return;
        }

        const [selectedKey] = Array.from(keys);
        if (selectedKey === undefined) {
            return;
        }

        const selectedNode = nodeById.get(Number(selectedKey));
        if (selectedNode) {
            onSelectCategory(selectedNode);
        }
    }, [nodeById, onSelectCategory]);

    const renderNode = (node: ClientCategoryTreeNode, level = 0): React.ReactNode => {
        const hasChildren = node.children.length > 0;
        return (
            <TreeItem
                key={node.id}
                id={node.id}
                textValue={node.name}
                value={node}
                className="binding-client-category-tree-item"
            >
                <TreeItemContent>
                    {({ isExpanded }) => (
                        <div
                            className={`binding-client-category-row ${selectedCategoryId === node.id ? 'selected' : ''}`}
                            style={{ '--tree-level': level } as React.CSSProperties}
                        >
                            {hasChildren ? (
                                <Button slot="chevron" className="binding-client-category-toggle">
                                    <span className="binding-client-category-chevron">
                                        {isExpanded ? 'v' : '>'}
                                    </span>
                                </Button>
                            ) : (
                                <span className="binding-client-category-placeholder" aria-hidden="true" />
                            )}
                            <span className="binding-client-category-name">{node.name}</span>
                        </div>
                    )}
                </TreeItemContent>

                {hasChildren && node.children.map(child => renderNode(child, level + 1))}
            </TreeItem>
        );
    };

    return (
        <Tree
            aria-label="Категории Лиры"
            className="binding-client-category-tree"
            expandedKeys={expandedKeys}
            onExpandedChange={setExpandedKeys}
            selectedKeys={selectedCategoryId === undefined ? [] : [selectedCategoryId]}
            onSelectionChange={handleSelectionChange}
            selectionMode="single"
            selectionBehavior="toggle"
        >
            {categories.map(category => renderNode(category))}
        </Tree>
    );
};

const CategorySelectionModal: React.FC<CategorySelectionModalProps> = ({
    integration,
    isOpen,
    onClose,
    currentUser
}) => {
    const [categories, setCategories] = useState<CategoryNodeDto[]>([]);
    const [categorySelections, setCategorySelections] = useState<CategorySelection>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [pullingCategories, setPullingCategories] = useState(false);
    const [clientCategories, setClientCategories] = useState<ClientCategoryTreeNode[]>([]);
    const [clientCategoriesLoading, setClientCategoriesLoading] = useState(false);
    const [clientCategoriesError, setClientCategoriesError] = useState<string | null>(null);
    const [selectedSupplierCategoryId, setSelectedSupplierCategoryId] = useState<number | undefined>(undefined);
    const [selectedClientCategoryId, setSelectedClientCategoryId] = useState<number | undefined>(undefined);
    const [bindingSaving, setBindingSaving] = useState(false);
    const canEdit = hasCapability(currentUser ?? null, 'categories.edit');

    const selectedSupplierCategory = useMemo(
        () => flattenSupplierCategories(categories).find(category => category.id === selectedSupplierCategoryId),
        [categories, selectedSupplierCategoryId]
    );

    const clientCategoryPathById = useMemo(
        () => buildClientCategoryPathMap(clientCategories),
        [clientCategories]
    );

    const selectedMappedCategory = selectedSupplierCategory?.mappedClientCategories?.[0];
    const selectedMappedCategoryPath = selectedMappedCategory
        ? clientCategoryPathById[selectedMappedCategory.id] ?? selectedMappedCategory.name
        : null;

    const loadCategories = useCallback(async () => {
        if (!integration) return;

        try {
            setLoading(true);
            const result = await api.get<IntegrationCategoriesDto[]>(`/api/categories?integrationId=${integration.id}`);
            buildTree(result.data);
            setCategories(result.data[0]?.categories || []);
        } catch (error) {
            console.error('Error loading categories:', error);
            setCategories([]);
        } finally {
            setLoading(false);
        }
    }, [integration]);

    const loadClientCategories = useCallback(async () => {
        setClientCategoriesLoading(true);
        setClientCategoriesError(null);

        try {
            const result = await api.get<ClientCategoryDto[]>('api/client-categories');
            setClientCategories(buildClientCategoryTree(result.data || []));
        } catch (error) {
            console.error('Error loading client categories:', error);
            setClientCategoriesError('Не удалось загрузить категории Лиры.');
        } finally {
            setClientCategoriesLoading(false);
        }
    }, []);

    const loadSelections = useCallback(async () => {
        if (!integration) return;

        try {
            const result = await api.get<number[]>(`/api/categories/selections?integrationId=${integration.id}`);
            const selectedCategoryIds = result.data || [];
            const categoryIdToExternalId = flattenSupplierCategories(categories).reduce((map, category) => {
                map[category.id] = category.externalId;
                return map;
            }, {} as Record<number, string>);
            const selections: CategorySelection = {};

            selectedCategoryIds.forEach(categoryId => {
                const externalId = categoryIdToExternalId[categoryId];
                if (externalId) {
                    selections[externalId] = true;
                }
            });

            setCategorySelections(selections);
        } catch (error) {
            console.error('Error loading selections:', error);
        }
    }, [integration, categories]);

    const handlePullCategories = async () => {
        if (!integration || pullingCategories) return;

        setPullingCategories(true);
        try {
            await api.post(`/api/jobs/pull-categories/${integration.id}`);
            toast.success('Обновление категорий запущено');
        } catch (error) {
            console.error('Error pulling categories:', error);
            toast.error('Неожиданная ошибка при загрузке категорий');
        } finally {
            setPullingCategories(false);
        }
    };

    useEffect(() => {
        if (!integration || !isOpen) return;

        setSelectedSupplierCategoryId(undefined);
        setSelectedClientCategoryId(undefined);
        void loadCategories();
        void loadClientCategories();
    }, [integration, isOpen, loadCategories, loadClientCategories]);

    useEffect(() => {
        if (categories.length > 0) {
            void loadSelections();
        }
    }, [categories, loadSelections]);

    const getAllDescendants = (node: CategoryNodeDto): string[] => {
        const descendants: string[] = [];
        if (node.externalId) descendants.push(node.externalId);
        node.children?.forEach(child => {
            descendants.push(...getAllDescendants(child));
        });
        return descendants;
    };

    const handleCheckboxChange = (categoryExternalId: string, isSelected: boolean) => {
        const node = flattenSupplierCategories(categories).find(category => category.externalId === categoryExternalId);
        if (!node) return;

        const categoriesToUpdate = node.children?.length
            ? getAllDescendants(node)
            : [categoryExternalId];

        setCategorySelections(prev => {
            const newSelections = { ...prev };
            categoriesToUpdate.forEach(extId => {
                newSelections[extId] = isSelected;
            });
            return newSelections;
        });
    };

    const handleSave = useCallback(async () => {
        if (!integration) return;

        try {
            setSaving(true);

            const selections = Object.keys(categorySelections)
                .filter(extId => categorySelections[extId])
                .map(categoryExternalId => ({
                    CategoryExternalId: categoryExternalId,
                    IsSelected: true
                }));

            const allCategories = flattenSupplierCategories(categories);
            allCategories.forEach(category => {
                if (!selections.some(selection => selection.CategoryExternalId === category.externalId)) {
                    selections.push({
                        CategoryExternalId: category.externalId,
                        IsSelected: false
                    });
                }
            });

            await api.post('/api/categories/selections', {
                IntegrationId: integration.id,
                Selections: selections
            });

            toast.success('Категории успешно сохранены');
            onClose();
        } catch (error) {
            console.error('Error saving selections:', error);
            toast.error('Ошибка при сохранении категорий: ' + (error instanceof Error ? error.message : 'Неизвестная ошибка'));
        } finally {
            setSaving(false);
        }
    }, [integration, categories, categorySelections, onClose]);

    const handleBindCategory = useCallback(async () => {
        if (!selectedSupplierCategory || !selectedClientCategoryId || bindingSaving) return;

        setBindingSaving(true);
        try {
            await api.post(
                `api/client-categories/${selectedClientCategoryId}/mappings/${selectedSupplierCategory.id}`,
                {}
            );

            const mappedClientCategoryName = clientCategoryPathById[selectedClientCategoryId]
                ?? selectedClientCategoryId.toString();

            setCategories(current => updateSupplierCategory(current, selectedSupplierCategory.id, category => ({
                ...category,
                mappedClientCategoryIds: selectedClientCategoryId.toString(),
                mappedClientCategories: [{
                    id: selectedClientCategoryId,
                    name: mappedClientCategoryName
                }]
            })));
            setSelectedClientCategoryId(undefined);
            toast.success('Категория поставщика привязана');
        } catch (error) {
            console.error('Error binding supplier category:', error);
            toast.error(error instanceof Error ? error.message : 'Не удалось привязать категорию поставщика');
        } finally {
            setBindingSaving(false);
        }
    }, [bindingSaving, clientCategoryPathById, selectedClientCategoryId, selectedSupplierCategory]);

    const handleUnbindCategory = useCallback(async () => {
        if (!selectedSupplierCategory || !selectedMappedCategory || bindingSaving) return;

        setBindingSaving(true);
        try {
            await api.delete(
                `api/client-categories/${selectedMappedCategory.id}/mappings/${selectedSupplierCategory.id}`
            );

            setCategories(current => updateSupplierCategory(current, selectedSupplierCategory.id, category => ({
                ...category,
                mappedClientCategoryIds: undefined,
                mappedClientCategories: undefined
            })));
            toast.success('Категория поставщика отвязана');
        } catch (error) {
            console.error('Error unbinding supplier category:', error);
            toast.error(error instanceof Error ? error.message : 'Не удалось отвязать категорию поставщика');
        } finally {
            setBindingSaving(false);
        }
    }, [bindingSaving, selectedMappedCategory, selectedSupplierCategory]);

    const renderCategoryTree = (supplierCategories: CategoryNodeDto[]): React.ReactElement[] => {
        return supplierCategories.map(category => (
            <SupplierCategoryNode
                key={category.id}
                node={category}
                selections={categorySelections}
                selectedCategoryId={selectedSupplierCategoryId}
                clientCategoryPathById={clientCategoryPathById}
                onSelectCategory={(supplierCategory) => {
                    setSelectedSupplierCategoryId(supplierCategory.id);
                    setSelectedClientCategoryId(undefined);
                }}
                onSelectionChange={handleCheckboxChange}
            />
        ));
    };

    const handleModalClick = (event: React.MouseEvent) => {
        event.stopPropagation();
    };

    if (!isOpen || !integration) return null;

    return (
        <div className="modal-backdrop-custom" onClick={onClose}>
            <div
                className={`modal-dialog-custom category-selection-dialog ${selectedSupplierCategory ? 'with-binding-panel' : ''} animate-in`}
                onClick={handleModalClick}
            >
                <div className="modal-content-custom">
                    <div className="modal-header-custom">
                        <div className="modal-title-custom">
                            <h3>Выбор категорий: {integration.name}</h3>
                        </div>
                        <button
                            className="btn-close-custom"
                            onClick={onClose}
                            title="Закрыть"
                        />
                    </div>

                    <div className="modal-body-custom category-selection-body">
                        <div className="category-selection-main-panel">
                            {loading ? (
                                <div className="text-center">
                                    <div className="btn-spinner" />
                                    <p>Загрузка категорий...</p>
                                </div>
                            ) : categories.length === 0 ? (
                                <div className="text-center">
                                    <p>Категории не найдены для этой интеграции</p>
                                </div>
                            ) : (
                                <div className="categories-tree-container">
                                    {renderCategoryTree(categories)}
                                </div>
                            )}
                        </div>

                        {selectedSupplierCategory && (
                            <aside className="category-binding-panel" aria-label="Привязка к категории Лиры">
                                <div className="category-binding-header">
                                    <h4>Привязка к НЛ</h4>
                                    <button
                                        type="button"
                                        className="category-binding-close"
                                        onClick={() => setSelectedSupplierCategoryId(undefined)}
                                        aria-label="Закрыть панель"
                                    >
                                        x
                                    </button>
                                </div>

                                <div className="category-binding-summary">
                                    <div className="category-binding-name">{selectedSupplierCategory.name}</div>
                                    {selectedSupplierCategory.externalId && (
                                        <div className="category-binding-external-id">{selectedSupplierCategory.externalId}</div>
                                    )}
                                </div>

                                {selectedMappedCategory ? (
                                    <div className="category-binding-current">
                                        <span className="category-binding-label">Текущая категория Лиры</span>
                                        <strong>{selectedMappedCategoryPath}</strong>
                                        {canEdit ? (
                                            <button
                                                type="button"
                                                className="btn btn-danger category-binding-action"
                                                onClick={handleUnbindCategory}
                                                disabled={bindingSaving}
                                            >
                                                {bindingSaving ? 'Отвязка...' : 'Отвязать'}
                                            </button>
                                        ) : (
                                            <div className="category-binding-readonly">Недостаточно прав для изменения привязки</div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="category-binding-editor">
                                        <span className="category-binding-label">Категория Лиры</span>
                                        {clientCategoriesLoading && (
                                            <div className="category-binding-state">
                                                <span className="btn-spinner" />
                                                <span>Загрузка...</span>
                                            </div>
                                        )}
                                        {!clientCategoriesLoading && clientCategoriesError && (
                                            <div className="category-binding-state error">{clientCategoriesError}</div>
                                        )}
                                        {!clientCategoriesLoading && !clientCategoriesError && clientCategories.length === 0 && (
                                            <div className="category-binding-state">Нет категорий для отображения</div>
                                        )}
                                        {!clientCategoriesLoading && !clientCategoriesError && clientCategories.length > 0 && (
                                            <ClientCategoryTree
                                                key={clientCategories.map(category => category.id).join('|')}
                                                categories={clientCategories}
                                                selectedCategoryId={selectedClientCategoryId}
                                                onSelectCategory={(category) => setSelectedClientCategoryId(category.id)}
                                            />
                                        )}
                                        {canEdit ? (
                                            <button
                                                type="button"
                                                className="btn btn-primary-custom category-binding-action"
                                                onClick={handleBindCategory}
                                                disabled={!selectedClientCategoryId || bindingSaving}
                                            >
                                                {bindingSaving ? 'Привязка...' : 'Привязать'}
                                            </button>
                                        ) : (
                                            <div className="category-binding-readonly">Недостаточно прав для изменения привязки</div>
                                        )}
                                    </div>
                                )}
                            </aside>
                        )}
                    </div>

                    <div className="modal-footer-custom">
                        <div className="d-flex justify-content align-items-center w-100">
                            <button
                                className="btn btn-secondary-custom"
                                onClick={handlePullCategories}
                                disabled={saving || pullingCategories}
                            >
                                {pullingCategories ? 'Запуск...' : 'Запустить обновление категорий'}
                            </button>
                            <div className="d-flex gap-2">
                                <button
                                    className="btn btn-secondary-custom"
                                    onClick={onClose}
                                    disabled={saving}
                                >
                                    Отмена
                                </button>
                                <button
                                    className="btn btn-primary-custom"
                                    onClick={handleSave}
                                    disabled={loading || saving || categories.length === 0}
                                >
                                    {saving && <div className="btn-spinner" />}
                                    Сохранить
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CategorySelectionModal;
